import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { formatLocalDate } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  Save,
  DoorOpen,
  DoorClosed,
  Calendar,
  CalendarDays,
  Loader2,
  Repeat,
} from 'lucide-react';

interface Assignment {
  date: string;
  dayName: string;
  id: number | null;
  openUserId: number | null;
  closeUserId: number | null;
}

interface UserOption {
  id: number;
  fullName: string;
  email: string;
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAY_LABELS: Record<string, string> = {
  domingo: 'Dom',
  lunes: 'Lun',
  martes: 'Mar',
  'miércoles': 'Mié',
  jueves: 'Jue',
  viernes: 'Vie',
  'sábado': 'Sáb',
};

const DAY_COLORS: Record<string, string> = {
  domingo: 'bg-red-50 border-red-200',
  lunes: 'bg-blue-50 border-blue-200',
  martes: 'bg-green-50 border-green-200',
  'miércoles': 'bg-yellow-50 border-yellow-200',
  jueves: 'bg-purple-50 border-purple-200',
  viernes: 'bg-orange-50 border-orange-200',
  'sábado': 'bg-pink-50 border-pink-200',
};

export default function AperturaCierre() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [localAssignments, setLocalAssignments] = useState<Assignment[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeView, setActiveView] = useState<'weekly' | 'monthly'>('monthly');
  const [currentWeekStart, setCurrentWeekStart] = useState(0);

  const canEdit = user?.permissions?.includes('apertura-cierre:edit') || false;

  const { data: usersData } = useQuery({
    queryKey: ['school-access-users'],
    queryFn: async () => {
      const res = await axios.get('/api/school-access/users-list');
      return res.data as UserOption[];
    },
  });

  const { data: assignmentsData, isLoading } = useQuery({
    queryKey: ['school-access', year, month],
    queryFn: async () => {
      const res = await axios.get(`/api/school-access/${year}/${month}`);
      return res.data as { assignments: Assignment[]; autoGenerated: boolean };
    },
  });

  useEffect(() => {
    if (assignmentsData?.assignments) {
      setLocalAssignments(assignmentsData.assignments);
      setHasChanges(false);
    }
  }, [assignmentsData]);

  const saveMutation = useMutation({
    mutationFn: async (assignments: Assignment[]) => {
      const res = await axios.post('/api/school-access/save-month', { assignments });
      return res.data;
    },
    onSuccess: (data) => {
      toast({ title: 'Guardado', description: data.message });
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['school-access', year, month] });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudieron guardar los cambios', variant: 'destructive' });
    },
  });

  const REPLICATE_WEEKS_FORWARD = 12;

  const replicateWeekMutation = useMutation({
    mutationFn: async (referenceWeekStart: string) => {
      const res = await axios.post('/api/school-access/replicate-week', { referenceWeekStart, weeksForward: REPLICATE_WEEKS_FORWARD });
      return res.data;
    },
    onSuccess: (data) => {
      toast({ title: 'Semana replicada', description: data.message });
      queryClient.invalidateQueries({ queryKey: ['school-access'] });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo replicar la semana', variant: 'destructive' });
    },
  });

  const handleUserChange = (date: string, field: 'openUserId' | 'closeUserId', value: number | null) => {
    setLocalAssignments(prev =>
      prev.map(a => a.date === date ? { ...a, [field]: value } : a)
    );
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(localAssignments);
  };

  const goToPrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const goToNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const weeklyData = useMemo(() => {
    if (!localAssignments.length) return [];
    const weeks: Assignment[][] = [];
    let currentWeek: Assignment[] = [];

    const firstDayIndex = new Date(localAssignments[0].date + 'T12:00:00Z').getUTCDay();
    for (let i = 0; i < firstDayIndex; i++) {
      currentWeek.push({ date: '', dayName: '', id: null, openUserId: null, closeUserId: null });
    }

    localAssignments.forEach((a) => {
      currentWeek.push(a);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    });

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push({ date: '', dayName: '', id: null, openUserId: null, closeUserId: null });
      }
      weeks.push(currentWeek);
    }

    return weeks;
  }, [localAssignments]);

  const currentWeekData = useMemo(() => {
    if (activeView !== 'weekly') return [];
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    if (currentWeekStart === 0 && weeklyData.length > 0) {
      const weekIdx = weeklyData.findIndex(week => week.some(d => d.date === todayStr));
      if (weekIdx >= 0) {
        return weeklyData[weekIdx];
      }
      return weeklyData[0];
    }

    if (currentWeekStart >= 0 && currentWeekStart < weeklyData.length) {
      return weeklyData[currentWeekStart];
    }
    return weeklyData[0] || [];
  }, [activeView, weeklyData, currentWeekStart]);

  const currentWeekMondayDate = useMemo(() => {
    const monday = currentWeekData.find(a => a.dayName === 'lunes' && a.date);
    return monday?.date || null;
  }, [currentWeekData]);

  const canReplicateWeek = canEdit && activeView === 'weekly' && assignmentsData?.autoGenerated === false && !!currentWeekMondayDate;

  const handleReplicateWeek = () => {
    if (!currentWeekMondayDate) return;

    // El patrón de esta semana SOBRESCRIBE cualquier asignación ya personalizada en el
    // rango destino (ver replicate-week en el backend) — se confirma explícitamente antes
    // de disparar la mutación, nombrando el rango de fechas que se va a sobrescribir.
    // El backend escribe de referenceWeekStart+7 (la semana de referencia misma NO se
    // toca) hasta referenceWeekStart + weeksForward*7 + 6 (el domingo de la última
    // semana replicada) — el rango mostrado debe coincidir exactamente con eso.
    const [y, m, d] = currentWeekMondayDate.split('-').map(Number);
    const startDate = new Date(Date.UTC(y, m - 1, d));
    startDate.setUTCDate(startDate.getUTCDate() + 7);
    const startDateStr = startDate.toISOString().split('T')[0];

    const endDate = new Date(Date.UTC(y, m - 1, d));
    endDate.setUTCDate(endDate.getUTCDate() + REPLICATE_WEEKS_FORWARD * 7 + 6);
    const endDateStr = endDate.toISOString().split('T')[0];

    const confirmed = window.confirm(
      `Se sobrescribirán las asignaciones del ${formatLocalDate(startDateStr)} al ${formatLocalDate(endDateStr)} ` +
      `con el patrón de esta semana. ¿Continuar?`
    );
    if (!confirmed) return;

    replicateWeekMutation.mutate(currentWeekMondayDate);
  };

  const getUserName = (userId: number | null) => {
    if (!userId || !usersData) return null;
    const u = usersData.find(u => u.id === userId);
    return u?.fullName || null;
  };

  const isToday = (dateStr: string) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return dateStr === todayStr;
  };

  const isWeekend = (dayName: string) => dayName === 'domingo';

  const UserSelect = ({ value, onChange, placeholder, disabled }: {
    value: number | null;
    onChange: (val: number | null) => void;
    placeholder: string;
    disabled: boolean;
  }) => (
    <select
      className="w-full text-xs sm:text-sm border rounded px-1.5 py-1 bg-white focus:ring-2 focus:ring-blue-300 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed truncate"
      value={value ?? ''}
      onChange={e => onChange(e.target.value ? parseInt(e.target.value) : null)}
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      {usersData?.map(u => (
        <option key={u.id} value={u.id}>{u.fullName}</option>
      ))}
    </select>
  );

  const DayCard = ({ assignment, compact = false }: { assignment: Assignment; compact?: boolean }) => {
    if (!assignment.date) return <div className="min-h-[100px]" />;

    const dayNum = parseInt(assignment.date.split('-')[2]);
    const today = isToday(assignment.date);
    const weekend = isWeekend(assignment.dayName);
    const dayColor = DAY_COLORS[assignment.dayName] || 'bg-gray-50 border-gray-200';

    return (
      <div className={`rounded-lg border-2 p-2 sm:p-3 transition-all ${today ? 'ring-2 ring-blue-500 border-blue-400 bg-blue-50' : weekend ? 'bg-gray-100 border-gray-300 opacity-60' : dayColor}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <span className={`text-sm sm:text-base font-bold ${today ? 'text-blue-700' : 'text-gray-700'}`}>
              {dayNum}
            </span>
            <span className="text-xs text-gray-500 capitalize">
              {DAY_LABELS[assignment.dayName] || assignment.dayName}
            </span>
          </div>
          {today && (
            <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-blue-600">Hoy</Badge>
          )}
        </div>

        <div className="space-y-2">
          <div>
            <div className="flex items-center gap-1 mb-1">
              <DoorOpen className="h-3 w-3 text-green-600" />
              <span className="text-[10px] sm:text-xs font-semibold text-green-700">Apertura</span>
            </div>
            {canEdit ? (
              <UserSelect
                value={assignment.openUserId}
                onChange={val => handleUserChange(assignment.date, 'openUserId', val)}
                placeholder="Seleccionar..."
                disabled={weekend}
              />
            ) : (
              <div className="text-xs sm:text-sm px-1.5 py-1 bg-white/50 rounded truncate">
                {getUserName(assignment.openUserId) || <span className="text-gray-400 italic">Sin asignar</span>}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <DoorClosed className="h-3 w-3 text-red-600" />
              <span className="text-[10px] sm:text-xs font-semibold text-red-700">Cierre</span>
            </div>
            {canEdit ? (
              <UserSelect
                value={assignment.closeUserId}
                onChange={val => handleUserChange(assignment.date, 'closeUserId', val)}
                placeholder="Seleccionar..."
                disabled={weekend}
              />
            ) : (
              <div className="text-xs sm:text-sm px-1.5 py-1 bg-white/50 rounded truncate">
                {getUserName(assignment.closeUserId) || <span className="text-gray-400 italic">Sin asignar</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Apertura y Cierre</h1>
          <p className="text-sm text-gray-500 mt-1">Asignación de responsables de apertura y cierre de la escuela</p>
        </div>

        <div className="flex items-center gap-2">
          {hasChanges && canEdit && (
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={goToPrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <CardTitle className="text-lg">
                {MONTH_NAMES[month - 1]} {year}
              </CardTitle>
              <Button variant="outline" size="sm" onClick={goToNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <Tabs value={activeView} onValueChange={(v) => setActiveView(v as 'weekly' | 'monthly')}>
              <TabsList>
                <TabsTrigger value="weekly" className="gap-1">
                  <Calendar className="h-4 w-4" />
                  Semanal
                </TabsTrigger>
                <TabsTrigger value="monthly" className="gap-1">
                  <CalendarDays className="h-4 w-4" />
                  Mensual
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {assignmentsData?.autoGenerated && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs sm:text-sm text-amber-800">
                Las asignaciones fueron auto-generadas basándose en los horarios de los profesores con permiso de apertura/cierre. 
                {canEdit ? ' Revise y guarde para confirmar.' : ''}
              </p>
            </div>
          )}
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3 text-gray-500">Cargando asignaciones...</span>
            </div>
          ) : activeView === 'monthly' ? (
            <div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                  <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">{d}</div>
                ))}
              </div>
              <div className="space-y-1">
                {weeklyData.map((week, wIdx) => (
                  <div key={wIdx} className="grid grid-cols-7 gap-1">
                    {week.map((a, dIdx) => (
                      <DayCard key={`${wIdx}-${dIdx}`} assignment={a} compact />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentWeekStart(prev => Math.max(0, prev - 1))}
                  disabled={currentWeekStart <= 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Semana anterior
                </Button>
                <span className="text-sm font-medium text-gray-600">
                  Semana {currentWeekStart + 1} de {weeklyData.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentWeekStart(prev => Math.min(weeklyData.length - 1, prev + 1))}
                  disabled={currentWeekStart >= weeklyData.length - 1}
                >
                  Semana siguiente
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {canReplicateWeek && (
                <div className="mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReplicateWeek}
                    disabled={replicateWeekMutation.isPending}
                    className="gap-1.5"
                  >
                    {replicateWeekMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Repeat className="h-4 w-4" />
                    )}
                    Replicar esta semana a los próximos 3 meses
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                  <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {currentWeekData.map((a, idx) => (
                  <DayCard key={idx} assignment={a} />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <DoorOpen className="h-3 w-3 text-green-600" />
          <span>Apertura: Responsable de abrir la escuela</span>
        </div>
        <div className="flex items-center gap-1">
          <DoorClosed className="h-3 w-3 text-red-600" />
          <span>Cierre: Responsable de cerrar la escuela</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-200 border border-blue-400" />
          <span>Día actual</span>
        </div>
      </div>
    </div>
  );
}
